import React from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { notEmptyValidator } from "commons/utils/validate";

import { useBlockGroupContext } from "components/form/Blocks";
import { AttachmentType } from "components/ui/AttachmentPicker";

import { useSaveActionsContext } from "context/save";

import useSectionTwoApi from "hooks/api/private/blocks/useSectionTwoApi";
import useMobileOrDesktopFile from "hooks/useMobileOrDesktopFile";
import { ButtonDeleteInterface } from "hooks/useTitleFunctionsTopBlock";

import { SectionTwoBody, SectionTwoDetails } from "types/api/blocks/sectionTwo";
import { DesktopMobileForm } from "types/api/commons";
import { BlockEnum, DesktopOrMobile } from "types/enums";

import { converterAttachmentToFileWithPayload, createAttachmentType } from "utils/utils";
import { getExtraErrorsDescription, notEmptyMedia, possibleEmptyMedia } from "utils/validators";

export type SectionTwoForm = {
    id: string | undefined;
    left_img_id: string | undefined;
    right_img_id: string | undefined;
    background_img_id: string | undefined;
    left_img: DesktopMobileForm;
    right_img: DesktopMobileForm;
    background_img: DesktopMobileForm;
    left_video: string;
    right_video: string;
};

type SectionTwoType = {
    setBackgroundImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    setRightImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    setLeftImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    buttonDelete: ButtonDeleteInterface;
    formHook: FormHookType<SectionTwoForm>;
};

function useSectionTwo(): SectionTwoType {
    const { saveSectionTwo, getOneSectionTwo, deleteSectionTwo } = useSectionTwoApi();

    const { blockGroupCompositionId, blockGroupId, blockId, graphicalOrder } =
        useBlockGroupContext();

    const isVideoBlock = React.useMemo(() => blockId === BlockEnum.SectionVideoCompany, [blockId]);

    const [schema, setSchema] = React.useState<ObjectPropertyType>({
        type: "object",
        properties: {
            left_video: {
                type: "simple",
                validators: isVideoBlock ? [notEmptyValidator] : [],
            },
            right_video: {
                type: "simple",
                validators: isVideoBlock ? [notEmptyValidator] : [],
            },
            background_img: possibleEmptyMedia,
            left_img: isVideoBlock ? possibleEmptyMedia : notEmptyMedia,
            right_img: isVideoBlock ? possibleEmptyMedia : notEmptyMedia,
        },
    });

    const initialValues: SectionTwoForm = React.useMemo(() => {
        return {
            id: undefined,
            left_img_id: undefined,
            left_img: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
            right_img_id: undefined,
            right_img: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
            background_img_id: undefined,
            background_img: {
                id: undefined,
                desktop: createAttachmentType({ id: null, label: "Desktop" }),
                mobile: createAttachmentType({ id: null, label: "Mobile" }),
            },
            left_video: "",
            right_video: "",
        };
    }, []);

    const formHook = useForm<SectionTwoForm>(schema, initialValues, getExtraErrorsDescription);

    const { showAllError, setForm, isValid, form } = formHook;

    React.useEffect(() => {
        setSchema((prevSchema) => {
            const newSchema = _.cloneDeep(prevSchema);

            if (!isVideoBlock) {
                if (form.left_video) {
                    newSchema.properties.left_img = possibleEmptyMedia;
                } else {
                    newSchema.properties.left_img = notEmptyMedia;
                }

                if (form.right_video) {
                    newSchema.properties.right_img = possibleEmptyMedia;
                } else {
                    newSchema.properties.right_img = notEmptyMedia;
                }
            }

            return newSchema;
        });
    }, [form.left_video, form.right_video, isVideoBlock]);

    const { setDesktopMobileImage: setBackgroundImage } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "background_img",
    });

    const { setDesktopMobileImage: setLeftImage } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "left_img",
    });

    const { setDesktopMobileImage: setRightImage } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "right_img",
    });

    const [sectionTwoDetails, setSectionTwoDetails] = React.useState<SectionTwoDetails | undefined>(
        undefined,
    );

    React.useEffect(() => {
        const [apiCall, abortController] = getOneSectionTwo(
            { id: blockGroupCompositionId },
            { rejectNotFound: true },
        );

        apiCall.then(setSectionTwoDetails).catch((error) => console.log(error));

        return () => abortController.abort();
    }, [setSectionTwoDetails, getOneSectionTwo, blockGroupCompositionId]);

    React.useEffect(() => {
        if (sectionTwoDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.id = sectionTwoDetails.id;
                newForm.background_img_id = sectionTwoDetails.background_img_id;
                newForm.left_img_id = sectionTwoDetails.left_img_id;
                newForm.right_img_id = sectionTwoDetails.right_img_id;
                newForm.left_video = sectionTwoDetails.left_video ?? "";
                newForm.right_video = sectionTwoDetails.right_video ?? "";

                if (sectionTwoDetails.background_img) {
                    newForm.background_img = {
                        id: sectionTwoDetails.background_img.id,
                        desktop: createAttachmentType({
                            id: sectionTwoDetails.background_img.desktop.id,
                            label: "Desktop",
                            filename: sectionTwoDetails.background_img.desktop.filename,
                            status: 2,
                        }),
                        mobile: createAttachmentType({
                            id: sectionTwoDetails.background_img.mobile.id,
                            label: "Mobile",
                            filename: sectionTwoDetails.background_img.mobile.filename,
                            status: 2,
                        }),
                    };
                }

                if (sectionTwoDetails.left_img) {
                    newForm.left_img = {
                        id: sectionTwoDetails.left_img.id,
                        desktop: createAttachmentType({
                            id: sectionTwoDetails.left_img.desktop.id,
                            label: "Desktop",
                            filename: sectionTwoDetails.left_img.desktop.filename,
                            status: 2,
                        }),
                        mobile: createAttachmentType({
                            id: sectionTwoDetails.left_img.mobile.id,
                            label: "Mobile",
                            filename: sectionTwoDetails.left_img.mobile.filename,
                            status: 2,
                        }),
                    };
                }

                if (sectionTwoDetails.right_img) {
                    newForm.right_img = {
                        id: sectionTwoDetails.right_img.id,
                        desktop: createAttachmentType({
                            id: sectionTwoDetails.right_img.desktop.id,
                            label: "Desktop",
                            filename: sectionTwoDetails.right_img.desktop.filename,
                            status: 2,
                        }),
                        mobile: createAttachmentType({
                            id: sectionTwoDetails.right_img.mobile.id,
                            label: "Mobile",
                            filename: sectionTwoDetails.right_img.mobile.filename,
                            status: 2,
                        }),
                    };
                }

                return newForm;
            });
        }
    }, [setForm, sectionTwoDetails]);

    const { addSaveAction } = useSaveActionsContext();

    React.useEffect(() => {
        addSaveAction(blockGroupCompositionId, () => {
            if (isValid) {
                const promises: Promise<unknown>[] = [];

                const sectionTwoBody: SectionTwoBody = {
                    id: blockGroupCompositionId,
                    left_img: {
                        id: form.left_img.id,
                        desktop: converterAttachmentToFileWithPayload(form.left_img.desktop),
                        mobile: converterAttachmentToFileWithPayload(form.left_img.mobile),
                    },
                    right_img: {
                        id: form.right_img.id,
                        desktop: converterAttachmentToFileWithPayload(form.right_img.desktop),
                        mobile: converterAttachmentToFileWithPayload(form.right_img.mobile),
                    },
                    background_img: {},
                    left_video: form.left_video,
                    right_video: form.right_video,
                };

                let addImageOne = false;

                if (
                    form.background_img &&
                    form.background_img.desktop &&
                    form.background_img.mobile
                ) {
                    if (
                        form.background_img.desktop.filename &&
                        form.background_img.mobile.filename
                    ) {
                        sectionTwoBody.background_img = {
                            id: form.background_img?.id,
                            desktop: form.background_img
                                ? converterAttachmentToFileWithPayload(form.background_img.desktop)
                                : undefined,
                            mobile: form.background_img
                                ? converterAttachmentToFileWithPayload(form.background_img.mobile)
                                : undefined,
                        };

                        addImageOne = true;
                    }
                }

                if (addImageOne === false) {
                    sectionTwoBody.background_img_id = null;
                }

                const [apiCall] = saveSectionTwo(sectionTwoBody);

                promises.push(apiCall);

                return Promise.all(promises);
            } else {
                showAllError();
                return Promise.reject("INVALID_FORM");
            }
        });
    }, [
        showAllError,
        saveSectionTwo,
        addSaveAction,
        blockGroupCompositionId,
        graphicalOrder,
        blockGroupId,
        isValid,
        blockId,
        form,
    ]);

    return React.useMemo(
        (): SectionTwoType => ({
            buttonDelete: deleteSectionTwo,
            setBackgroundImage,
            setRightImage,
            setLeftImage,
            formHook,
        }),
        [deleteSectionTwo, setBackgroundImage, setRightImage, setLeftImage, formHook],
    );
}

export default useSectionTwo;
